import React, { useCallback, useEffect, useState } from 'react';
import { useDataProvider } from 'react-admin';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import Typography from '@material-ui/core/Typography';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { makeStyles } from '@material-ui/core/styles';
import { apiPost, apiPut, getZ3PConfiguration } from '../brainApi';
import ConfirmActionModal from '../components/ConfirmActionModal';
import {
  authorizeGoogle,
  getZ3PConfigValue,
  storeZ3PConfiguration,
} from '../helper';

import {
  ExpansionPanel,
  ExpansionPanelDetails,
  ExpansionPanelSummary,
} from './SettingsUtils';
import GoogleAuthModal from '../components/GoogleAuthModal';

const useStyles = makeStyles((theme) => ({
  settingsThemeContainer: {
    width: '50%',
    margin: '20px auto 0 auto',
    [theme.breakpoints.down('sm')]: {
      width: 'calc(100% - 25px) !important',
      margin: '10px auto 0px auto !important',
    },
  },
}));

const GoogleCalendar = () => {
  const dataProvider = useDataProvider();

  const [expanded, setExpanded] = useState(false);
  const [googleCalendarEmail, setGoogleCalendarEmail] = useState(null);
  const [googleCalendarIsAuthorized, setGoogleCalendarIsAuthorized] = useState(
    null,
  );
  const [selectedServiceVehicle, setSelectedServiceVehicle] = useState(null);
  const [serviceVehicles, setServiceVehicles] = useState([]);
  const [unlinkAccountModalOpen, setUnlinkAccountModalOpen] = useState(false);
  const [googleAuthModelOpen, setGoogleAuthModelOpen] = useState(false);
  const classes = useStyles();

  const fetchServiceVehicles = useCallback(async () => {
    const serviceVehiclesRes = await dataProvider.getList('service-vehicle', {
      filter: { is_active: true },
      sort: { field: 'created_at', order: 'ASC' },
      pagination: { page: 1, perPage: 500 },
    });
    setServiceVehicles(serviceVehiclesRes.data);

    const z3pConfiguration = await getZ3PConfiguration();
    storeZ3PConfiguration(z3pConfiguration);

    const selectedServiceVehicleMatch = serviceVehiclesRes.data.find(
      (sv) =>
        sv.id === z3pConfiguration.google_calendar_selected_service_vehicle,
    );
    setSelectedServiceVehicle(selectedServiceVehicleMatch);
  }, [dataProvider]);

  const onClickAuthorizeOrChange = () => {
    authorizeGoogle(() =>
      setGoogleAuthModelOpen(true, googleCalendarIsAuthorized || null),
    );
  };

  const showServiceVehicle =
    googleCalendarIsAuthorized &&
    !!serviceVehicles &&
    serviceVehicles.length > 1 &&
    selectedServiceVehicle;

  useEffect(() => {
    const googleCalendarEmailZ3P = getZ3PConfigValue('google_calendar_email');
    const googleCalendarIsAuthorizedZ3P = getZ3PConfigValue(
      'google_calendar_is_authorized',
    );
    setGoogleCalendarEmail(googleCalendarEmailZ3P);
    setGoogleCalendarIsAuthorized(googleCalendarIsAuthorizedZ3P);
  }, []);

  useEffect(() => {
    fetchServiceVehicles();
  }, [fetchServiceVehicles]);

  return (
    <>
      <div className={classes.settingsThemeContainer}>
        <ExpansionPanel
          square
          expanded={expanded}
          onChange={() => setExpanded(!expanded)}
        >
          <ExpansionPanelSummary
            aria-controls="panel1d-content"
            expandIcon={<ExpandMoreIcon />}
            id="panel1d-header"
          >
            <Typography>Google Calendar</Typography>
          </ExpansionPanelSummary>
          <ExpansionPanelDetails>
            <Box
              display="flex"
              alignItems="center"
              flexDirection="column"
              width="100%"
            >
              {googleCalendarIsAuthorized && (
                <Typography>
                  <b>Linked Google account:</b> {googleCalendarEmail}
                </Typography>
              )}
              {showServiceVehicle && (
                <>
                  <Box
                    alignItems="center"
                    display="flex"
                    gridGap={4}
                    style={{ paddingTop: 4, paddingBottom: 4 }}
                  >
                    <Typography style={{ fontWeight: 'bold' }}>
                      Linked Service{' '}
                      {selectedServiceVehicle.is_service_bay
                        ? 'Bay'
                        : 'Vehicle'}
                      :
                    </Typography>
                    <Select
                      id="service-bay-vehicle-select"
                      value={selectedServiceVehicle.id}
                      onChange={async (e) => {
                        const matchingSV = serviceVehicles.find(
                          (sv) => sv.id === e.target.value,
                        );

                        setSelectedServiceVehicle(matchingSV);

                        const res = await apiPut(
                          '/integration/google/selected-service-vehicle',
                          {
                            service_vehicle_id: matchingSV.id,
                          },
                        );

                        if (res.Status === 'Failure') {
                          alert(`Error:\n\n${res.Message}`);
                        }
                      }}
                    >
                      {serviceVehicles.map((sv) => (
                        <MenuItem id={sv.id} key={sv.id} value={sv.id}>
                          {sv.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>
                  <Typography align="center" variant="caption">
                    Events added to Google Calendar will impact the availability
                    of this service{' '}
                    {selectedServiceVehicle.is_service_bay ? 'bay' : 'vehicle'}
                  </Typography>
                </>
              )}
              <Button
                onClick={() =>
                  onClickAuthorizeOrChange(!!googleCalendarIsAuthorized)
                }
              >
                {googleCalendarIsAuthorized
                  ? 'Change linked account'
                  : 'Authorize Google'}
              </Button>
              {googleCalendarIsAuthorized && (
                <Button onClick={() => setUnlinkAccountModalOpen(true)}>
                  Unlink account
                </Button>
              )}
            </Box>
          </ExpansionPanelDetails>
        </ExpansionPanel>
      </div>
      <GoogleAuthModal
        onClose={() => setGoogleAuthModelOpen(false)}
        onSuccess={setGoogleCalendarEmail}
        open={googleAuthModelOpen}
      />
      <ConfirmActionModal
        open={unlinkAccountModalOpen}
        mainText="Are you sure you want to unlink this Google account?"
        subText="You will need to link your account again to send and receive appointment data from Google."
        confirmText="Unlink account"
        onConfirm={async () => {
          try {
            await apiPost('/integration/google/revoke-auth');
            // Must get z3p configuration since values will be updated
            const z3pConfiguration = await getZ3PConfiguration();
            storeZ3PConfiguration(z3pConfiguration);
            setGoogleCalendarEmail(false);
          } catch {
            console.error('Error in unlinking account');
          }

          setUnlinkAccountModalOpen(false);
        }}
        onCancel={(e) => {
          e.stopPropagation();
          setUnlinkAccountModalOpen(false);
        }}
      />
    </>
  );
};

export default GoogleCalendar;
